import { TAB_QUERY } from 'src/utils/common-types';

const ROOTS = {
  AUTH: '/auth',
  DASHBOARD: '/dashboard',
};

export const paths = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  faqs: '/faqs',
  page403: '/403',
  page404: '/404',
  page500: '/500',
  components: '/components',
  docs: 'https://docs.minimals.cc',
  changelog: 'https://docs.minimals.cc/changelog',
  zoneUI: 'https://mui.com/store/items/zone-landing-page/',
  pubsira: 'https://pubsira.com',
  // AUTH
  auth: {
    jwt: {
      login: `${ROOTS.AUTH}/login`,
      register: `${ROOTS.AUTH}/register`,
    },
  },
  // DASHBOARD
  dashboard: {
    // The default landing page after login (see PATH_AFTER_LOGIN) — My
    // Tasks, not Analytics. Analytics keeps its own explicit path below
    // so the nav item doesn't silently repoint if this ever changes again.
    root: `${ROOTS.DASHBOARD}/my-tasks`,
    myTasks: `${ROOTS.DASHBOARD}/my-tasks`,
    analytics: `${ROOTS.DASHBOARD}/analytics`,
    // permission: `${ROOTS.DASHBOARD}/permission`,
    // general: {
    //   app: `${ROOTS.DASHBOARD}/app`,
    //   ecommerce: `${ROOTS.DASHBOARD}/ecommerce`,
    //   analytics: `${ROOTS.DASHBOARD}/analytics`,
    //   banking: `${ROOTS.DASHBOARD}/banking`,
    //   booking: `${ROOTS.DASHBOARD}/booking`,
    //   file: `${ROOTS.DASHBOARD}/file`,
    // },
    user: {
      root: (defaultTab?: string) => `${ROOTS.DASHBOARD}/user?${TAB_QUERY}=${defaultTab ?? ''}`,
      new: `${ROOTS.DASHBOARD}/user/new`,
      list: `${ROOTS.DASHBOARD}/user/list`,
      cards: `${ROOTS.DASHBOARD}/user/cards`,
      profile: `${ROOTS.DASHBOARD}/user/profile`,
      account: `${ROOTS.DASHBOARD}/user/account`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/user/${id}/edit`,
      view: (id: string) => `${ROOTS.DASHBOARD}/user/${id}/view`,
    },
    kitchen: {
      root: (defaultTab?: string) => `${ROOTS.DASHBOARD}/kitchen?${TAB_QUERY}=${defaultTab ?? ''}`,
      new: `${ROOTS.DASHBOARD}/kitchen/new`,
      view: (id: string) => `${ROOTS.DASHBOARD}/kitchen/${id}/view`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/kitchen/${id}/edit`,
      purchase: {
        root: (defaultTab?: string) =>
          `${ROOTS.DASHBOARD}/kitchen-purchase?${TAB_QUERY}=${defaultTab ?? ''}`,
        new: `${ROOTS.DASHBOARD}/kitchen-purchase/new`,
        edit: (id: string) => `${ROOTS.DASHBOARD}/kitchen-purchase/${id}/edit`,
        view: (id: string) => `${ROOTS.DASHBOARD}/kitchen-purchase/${id}/view`,
      },
      dispatch: {
        root: (defaultTab?: string) =>
          `${ROOTS.DASHBOARD}/kitchen-dispatch?${TAB_QUERY}=${defaultTab ?? ''}`,
        new: `${ROOTS.DASHBOARD}/kitchen-dispatch/new`,
        edit: (id: string) => `${ROOTS.DASHBOARD}/kitchen-dispatch/${id}/edit`,
        view: (id: string) => `${ROOTS.DASHBOARD}/kitchen-dispatch/${id}/view`,
      },
    },
    bar: {
      root: (defaultTab?: string) => `${ROOTS.DASHBOARD}/bar?${TAB_QUERY}=${defaultTab ?? ''}`,
      new: `${ROOTS.DASHBOARD}/bar/new`,
      view: (id: string) => `${ROOTS.DASHBOARD}/bar/${id}/view`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/bar/${id}/edit`,
      purchase: {
        root: (defaultTab?: string) =>
          `${ROOTS.DASHBOARD}/bar-purchase?${TAB_QUERY}=${defaultTab ?? ''}`,
        new: `${ROOTS.DASHBOARD}/bar-purchase/new`,
        edit: (id: string) => `${ROOTS.DASHBOARD}/bar-purchase/${id}/edit`,
        view: (id: string) => `${ROOTS.DASHBOARD}/bar-purchase/${id}/view`,
      },
      dispatch: {
        root: (defaultTab?: string) =>
          `${ROOTS.DASHBOARD}/bar-dispatch?${TAB_QUERY}=${defaultTab ?? ''}`,
        new: `${ROOTS.DASHBOARD}/bar-dispatch/new`,
        edit: (id: string) => `${ROOTS.DASHBOARD}/bar-dispatch/${id}/edit`,
        view: (id: string) => `${ROOTS.DASHBOARD}/bar-dispatch/${id}/view`,
      },
    },
    cheque: {
      root: (defaultTab?: string) => `${ROOTS.DASHBOARD}/cheque?${TAB_QUERY}=${defaultTab ?? ''}`,
      new: `${ROOTS.DASHBOARD}/cheque/new`,
      view: (id: string) => `${ROOTS.DASHBOARD}/cheque/${id}/view`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/cheque/${id}/edit`,
    },
    issuedCheque: {
      root: `${ROOTS.DASHBOARD}/issued-cheque`,
      new: `${ROOTS.DASHBOARD}/issued-cheque/new`,
      view: (id: string) => `${ROOTS.DASHBOARD}/issued-cheque/${id}/view`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/issued-cheque/${id}/edit`,
    },
    chequeFactoring: {
      candidates: `${ROOTS.DASHBOARD}/cheque-factoring-candidates`,
      batches: {
        root: `${ROOTS.DASHBOARD}/cheque-factoring-batches`,
        view: (id: string) => `${ROOTS.DASHBOARD}/cheque-factoring-batches/${id}/view`,
      },
      overdue: `${ROOTS.DASHBOARD}/cheque-factoring-overdue`,
      debtors: `${ROOTS.DASHBOARD}/cheque-factoring-debtors`,
    },
    suppliers: {
      root: `${ROOTS.DASHBOARD}/suppliers`,
      new: `${ROOTS.DASHBOARD}/suppliers/new`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/suppliers/${id}/edit`,
      view: (id: string) => `${ROOTS.DASHBOARD}/suppliers/${id}/view`,
    },
    employees: {
      root: `${ROOTS.DASHBOARD}/employees`,
      new: `${ROOTS.DASHBOARD}/employees/new`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/employees/${id}/edit`,
      view: (id: string) => `${ROOTS.DASHBOARD}/employees/${id}/view`,
    },
    advancedPayments: {
      root: `${ROOTS.DASHBOARD}/advanced-payments`,
      new: (employeeID: string) => `${ROOTS.DASHBOARD}/advanced-payments/${employeeID}/new`,
      edit: (employeeID: string, id: string) =>
        `${ROOTS.DASHBOARD}/advanced-payments/${employeeID}/edit/${id}`,
      view: (id: string) => `${ROOTS.DASHBOARD}/advanced-payments/${id}/view`,
    },
    reorderSettings: {
      root: `${ROOTS.DASHBOARD}/reorder-settings`,
    },
    loans: {
      root: (defaultTab?: string) => `${ROOTS.DASHBOARD}/loans?${TAB_QUERY}=${defaultTab ?? ''}`,
      new: `${ROOTS.DASHBOARD}/loans/new`,
      view: (id: string) => `${ROOTS.DASHBOARD}/loans/${id}/view`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/loans/${id}/edit`,
    },
    auditLog: {
      root: `${ROOTS.DASHBOARD}/audit-log`,
    },
    packagingProduct: {
      root: `${ROOTS.DASHBOARD}/packaging-product`,
      new: `${ROOTS.DASHBOARD}/packaging-product/new`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/packaging-product/${id}/edit`,
    },
    packagingPurchaseOrder: {
      root: `${ROOTS.DASHBOARD}/packaging-purchase-order`,
      new: `${ROOTS.DASHBOARD}/packaging-purchase-order/new`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/packaging-purchase-order/${id}/edit`,
      view: (id: string) => `${ROOTS.DASHBOARD}/packaging-purchase-order/${id}/view`,
    },
    packagingDispatch: {
      root: `${ROOTS.DASHBOARD}/packaging-dispatch`,
      new: `${ROOTS.DASHBOARD}/packaging-dispatch/new`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/packaging-dispatch/${id}/edit`,
      view: (id: string) => `${ROOTS.DASHBOARD}/packaging-dispatch/${id}/view`,
    },
    packagingReturn: {
      root: `${ROOTS.DASHBOARD}/packaging-return`,
      new: `${ROOTS.DASHBOARD}/packaging-return/new`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/packaging-return/${id}/edit`,
      view: (id: string) => `${ROOTS.DASHBOARD}/packaging-return/${id}/view`,
    },
    packagingItem: {
      root: `${ROOTS.DASHBOARD}/packaging-item`,
      new: `${ROOTS.DASHBOARD}/packaging-item/new`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/packaging-item/${id}/edit`,
      view: (id: string) => `${ROOTS.DASHBOARD}/packaging-item/${id}/view`,
      usage: `${ROOTS.DASHBOARD}/packaging-item/usage`,
    },
    stationeryItem: {
      root: `${ROOTS.DASHBOARD}/stationery-item`,
      new: `${ROOTS.DASHBOARD}/stationery-item/new`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/stationery-item/${id}/edit`,
      view: (id: string) => `${ROOTS.DASHBOARD}/stationery-item/${id}/view`,
    },
    stationeryPurchaseOrder: {
      root: `${ROOTS.DASHBOARD}/stationery-purchase-order`,
      new: `${ROOTS.DASHBOARD}/stationery-purchase-order/new`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/stationery-purchase-order/${id}/edit`,
      view: (id: string) => `${ROOTS.DASHBOARD}/stationery-purchase-order/${id}/view`,
    },
    stationeryDispatch: {
      root: `${ROOTS.DASHBOARD}/stationery-dispatch`,
      new: `${ROOTS.DASHBOARD}/stationery-dispatch/new`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/stationery-dispatch/${id}/edit`,
      view: (id: string) => `${ROOTS.DASHBOARD}/stationery-dispatch/${id}/view`,
    },
    stationeryReturn: {
      root: `${ROOTS.DASHBOARD}/stationery-return`,
      new: `${ROOTS.DASHBOARD}/stationery-return/new`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/stationery-return/${id}/edit`,
      view: (id: string) => `${ROOTS.DASHBOARD}/stationery-return/${id}/view`,
    },
    packagingStockCount: {
      root: `${ROOTS.DASHBOARD}/packaging-stock-count`,
      new: `${ROOTS.DASHBOARD}/packaging-stock-count/new`,
      view: (id: string) => `${ROOTS.DASHBOARD}/packaging-stock-count/${id}/view`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/packaging-stock-count/${id}/edit`,
    },
    stationeryStockCount: {
      root: `${ROOTS.DASHBOARD}/stationery-stock-count`,
      new: `${ROOTS.DASHBOARD}/stationery-stock-count/new`,
      view: (id: string) => `${ROOTS.DASHBOARD}/stationery-stock-count/${id}/view`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/stationery-stock-count/${id}/edit`,
    },
    packagingDamageDispatch: {
      root: `${ROOTS.DASHBOARD}/packaging-damage-dispatch`,
      new: `${ROOTS.DASHBOARD}/packaging-damage-dispatch/new`,
      view: (id: string) => `${ROOTS.DASHBOARD}/packaging-damage-dispatch/${id}/view`,
    },
    stationeryDamageDispatch: {
      root: `${ROOTS.DASHBOARD}/stationery-damage-dispatch`,
      new: `${ROOTS.DASHBOARD}/stationery-damage-dispatch/new`,
      view: (id: string) => `${ROOTS.DASHBOARD}/stationery-damage-dispatch/${id}/view`,
    },
    sales: {
      root: `${ROOTS.DASHBOARD}/sales`,
      new: `${ROOTS.DASHBOARD}/sales/new`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/sales/${id}/edit`,
    },
    customer: {
      root: `${ROOTS.DASHBOARD}/contact`,
      new: `${ROOTS.DASHBOARD}/contact/new`,
      list: `${ROOTS.DASHBOARD}/contact/list`,
      cards: `${ROOTS.DASHBOARD}/contact/cards`,
      profile: `${ROOTS.DASHBOARD}/contact/profile`,
      account: `${ROOTS.DASHBOARD}/contact/account`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/contact/${id}/edit`,
    },
    product: {
      root: `${ROOTS.DASHBOARD}/inventory`,
      new: `${ROOTS.DASHBOARD}/inventory/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/inventory/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/inventory/${id}/edit`,
    },
    project: {
      root: `${ROOTS.DASHBOARD}/project`,
      new: `${ROOTS.DASHBOARD}/project/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/project/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/project/${id}/edit`,
    },
    invoice: {
      root: `${ROOTS.DASHBOARD}/invoice`,
      new: `${ROOTS.DASHBOARD}/invoice/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/invoice/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/invoice/${id}/edit`,
    },
    order: {
      root: `${ROOTS.DASHBOARD}/order`,
      details: (id: string) => `${ROOTS.DASHBOARD}/order/${id}`,
    },
  },
};
